import { useEffect, useState, type ComponentPropsWithoutRef } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getContent } from '../lib/api';
import { headingId } from '../lib/headingId';
import ShareableHeading from './ShareableHeading';
import { useGlossary } from '../contexts/GlossaryContext';
import '../styles/MarkdownPage.css';

interface MarkdownPageProps {
  page: string;
}
/** Custom heading renderers — every ## / ### in the .md file becomes shareable */
const MD_COMPONENTS: Components = {
  h1: ({ children, ...props }: ComponentPropsWithoutRef<'h1'>) => (
    <ShareableHeading id={headingId(children)} as="h1" {...(props as object)}>{children}</ShareableHeading>
  ),
  h2: ({ children, ...props }: ComponentPropsWithoutRef<'h2'>) => (
    <ShareableHeading id={headingId(children)} as="h2" {...(props as object)}>{children}</ShareableHeading>
  ),
  h3: ({ children, ...props }: ComponentPropsWithoutRef<'h3'>) => (
    <ShareableHeading id={headingId(children)} as="h3" {...(props as object)}>{children}</ShareableHeading>
  ),
  h4: ({ children, ...props }: ComponentPropsWithoutRef<'h4'>) => (
    <ShareableHeading id={headingId(children)} as="h4" {...(props as object)}>{children}</ShareableHeading>
  ),
};

export default function MarkdownPage({ page }: MarkdownPageProps) {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useGlossary();

  useEffect(() => {
    if (!page) return;

    const filename = `${page}.md`;
    setLoading(true);
    setError(null);

    getContent(filename)
      .then(markdown => {
        setContent(markdown);
        setLoading(false);
      })
      .catch(err => {
        console.error(`Failed to load ${filename}:`, err);
        setError(t('markdown.error', { page }));
        setLoading(false);
      });
  }, [page]);

  if (loading) return <div className="loading-page">{t('markdown.loading')}</div>;
  if (error) return <div className="error-page"><div className="error-box">{error}</div></div>;

  return (
    <div className="markdown-page">
      <div className="markdown-content">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
          {content}
        </ReactMarkdown>
      </div>
    </div>
  );
}
